// Boot before the actual exam controllers. The real page renders its real data;
// only persistence and account actions are isolated for the onboarding exercise.
export function setupCourseDemo() {
  if (window.parent === window || new URLSearchParams(location.search).get('courseDemo') !== '1') return;
  document.documentElement.dir = 'ltr';
  document.documentElement.lang = 'de';
  document.body.classList.add('zd-course-demo');
  window.ZDEUTSCH_COURSE_DEMO = true;
  const values = new Map();
  const memory = { getItem: key => values.get(String(key)) ?? null, setItem: (key, value) => values.set(String(key), String(value)), removeItem: key => values.delete(String(key)), clear: () => values.clear(), key: i => [...values.keys()][i] ?? null, get length() { return values.size; } };
  Object.defineProperty(window, 'localStorage', { value: memory });
  Object.defineProperty(window, 'sessionStorage', { value: memory });
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, options = {}) => {
    const url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, location.href);
    const method = String(options.method || input?.method || 'GET').toUpperCase();
    if (url.origin === location.origin && url.pathname === '/session') return Promise.resolve(Response.json({ ok: true, user: { displayName: 'Training', nativeLanguage: 'Deutsch', role: 'zdeutsch-user' } }));
    if (!['GET', 'HEAD'].includes(method)) return Promise.resolve(Response.json({ error: 'Diese Aktion ist im geführten Training nicht verfügbar.' }, { status: 403 }));
    if (url.pathname === '/sprechraeume') return Promise.resolve(Response.json({ ok: true, rooms: [] }));
    if (url.pathname === '/speaking/attempts') return Promise.resolve(Response.json({ ok: true, attempts: [] }));
    if (url.pathname.endsWith('/zdeutsch/writing/corrections')) return Promise.resolve(Response.json({ ok: true, corrections: [] }));
    return originalFetch(input, options);
  };
  const style = document.createElement('style');
  style.textContent = `.zd-course-demo .zd-reading-toolbar,.zd-course-demo .zd-reading-translation,.zd-course-demo .zd-word-modal,.zd-course-demo .zd-word-tooltip,.zd-course-demo .zd-translation-button,.zd-course-demo .zd-session-user,.zd-course-demo .zd-course-nav-link,.zd-course-demo #whatsapp-welcome-gate,.zd-course-demo .community-widget,.zd-course-demo .zd-data-loader{display:none!important}
  .zd-course-target{outline:3px solid #eebc32!important;outline-offset:4px;box-shadow:0 0 0 9px #facc152b!important;transition:outline-color .2s!important;scroll-margin:30px}
  .zd-course-pointer{position:fixed;z-index:2147483647;pointer-events:none;width:28px;height:28px;border-radius:50%;background:#edbd3b;color:#302d21;box-shadow:0 3px 15px #302d2144;display:grid;place-items:center;font:700 17px sans-serif;transform:translate(-50%,-50%)}
  @media(prefers-reduced-motion:no-preference){.zd-course-target{animation:course-target 1.6s ease-in-out infinite}@keyframes course-target{50%{outline-color:#a47503}}}`;
  document.head.append(style);
  const send = data => window.parent.postMessage({ source: 'zdeutsch-course-demo', ...data }, location.origin);
  let activeTarget = null, activeId = null, activePart = null, targetElement = null, focusTimer = null, scheduled = false;
  const pointer = document.createElement('div');
  pointer.className = 'zd-course-pointer'; pointer.textContent = '↖'; pointer.hidden = true; pointer.setAttribute('aria-hidden','true');
  document.body.append(pointer);
  const visible = element => Boolean(element && element.getClientRects().length && !element.closest('[aria-hidden="true"],[inert]') && getComputedStyle(element).visibility !== 'hidden');
  const selectors = {
    parts: '#part-cards button, #horen-part-list button, #part-list button, #part-tabs [data-part="teil-2"]',
    'reading-text': '#left-panel',
    'reading-situation': '#left-panel [data-reading-situation]',
    answer: '#right-panel button, #horen-content input[type=radio], input[type=radio]',
    check: '#right-panel button, .topic-check-btn, #horen-check-btn',
    settings: '#settings-btn',
    prompt: '.shreiben-task-columns .shreiben-info-block:last-of-type',
    write: '.shreiben-editor',
    characters: '.shreiben-editor-char-btn',
    topic: '#topic-list button, .oral-checklist-item',
    'speaking-task': '#practice-panel .oral-practice-head',
    'speaking-text': '#practice-panel .oral-topic-reading',
    'listening-task': '#horen-content .horen-topic',
    timer: '#timer-toggle',
    complete: '[data-action=toggle-complete]'
  };
  function findTarget(action) {
    if(activePart && document.querySelector('#left-panel')?.dataset.partKey!==activePart)return null;
    if(action==='answer' && document.querySelector('#part-cards'))return [...document.querySelectorAll('[data-course-answer]')].find(visible);
    if (action === 'check') return [...document.querySelectorAll('button')].find(el => visible(el) && /^(Antworten prüfen|Prüfen|Check)$/i.test(el.textContent.trim())) || [...document.querySelectorAll('[data-course-result]')].find(visible);
    if (action === 'characters') return [...document.querySelectorAll('button')].find(el => visible(el) && el.textContent.trim() === 'ä');
    return [...document.querySelectorAll(selectors[action] || 'body')].find(visible);
  }
  function prepare(action) {
    if(activePart && document.querySelector('#left-panel')?.dataset.partKey!==activePart)document.querySelector(`#part-cards [data-part-key="${activePart}"]`)?.click();
    const settings = document.querySelector('#settings-btn');
    if (action !== 'settings' && settings?.getAttribute('aria-expanded') === 'true') settings.click();
    const aside = document.querySelector('#aside-toggle');
    const part = document.querySelector('#left-panel')?.dataset.partKey;
    const wantsQuestions = action==='check' ? ['teil-1','teil-2','sprachbausteine-2'].includes(part) : action==='answer' && ['teil-2','sprachbausteine-2'].includes(part);
    if (wantsQuestions && visible(aside) && aside.getAttribute('aria-expanded') !== 'true') aside.click();
    if (!wantsQuestions && visible(aside) && aside.getAttribute('aria-expanded') === 'true') aside.click();
    if (action === 'prompt' && !visible(document.querySelector('.shreiben-task-columns'))) document.querySelector('.shreiben-fullscreen-toggle')?.click();
    if (['write','characters','settings'].includes(action) && document.body.classList.contains('shreiben-mobile-task-open')) document.querySelector('.shreiben-mobile-editor-toggle')?.click();
    if (['topic','complete'].includes(action) && !document.querySelector('#topic-list [data-topic-id]')) document.querySelector('#part-tabs [data-part="teil-2"]')?.click();
  }
  function positionPointer() {
    scheduled = false;
    if (!activeTarget) {pointer.style.display='none';return;}
    const target = findTarget(activeTarget);
    if (target !== targetElement) {
      targetElement?.classList.remove('zd-course-target');
      targetElement = target;
      targetElement?.classList.add('zd-course-target');
    }
    if (!target) {pointer.style.display='none';return;}
    const rect = target.getBoundingClientRect();
    const y = Math.max(14,rect.top + Math.min(rect.height / 2,22));
    const x = Math.min(innerWidth-14,Math.max(14,rect.right-8));
    pointer.style.display = rect.bottom>0 && rect.top<innerHeight && rect.right>0 ? 'grid' : 'none';
    pointer.style.left=x+'px'; pointer.style.top=y+'px';
  }
  function schedulePosition() {
    if (!scheduled) {scheduled=true;requestAnimationFrame(positionPointer);}
  }
  function highlight(action, id, part) {
    clearTimeout(focusTimer);
    activeTarget=action; activeId=id; activePart=part || null;
    prepare(action);
    const target=findTarget(action);
    if(action==='prompt' && target)target.tabIndex=0;
    // Keep the real sticky exam header clear of the narrated element.
    if(target)target.style.scrollMarginTop=Math.max(32,(document.querySelector('header')?.getBoundingClientRect().height||0)+20)+'px';
    target?.scrollIntoView({block:target.offsetHeight>innerHeight*.75?'start':'center',behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    schedulePosition();
    focusTimer=setTimeout(()=>{
      if(activeId!==id)return;
      const current=findTarget(action), rect=current?.getBoundingClientRect();
      send({type:'highlighted',id,action,found:Boolean(current),rect:rect?{top:rect.top,height:rect.height}:null});
    },600);
  }
  let announcedReady=false;
  function ready(force=false) {
    if((force || !announcedReady) && document.querySelector('#part-cards button,#horen-content input,#shreiben-content textarea,#practice-panel button')) {announcedReady=true;send({type:'ready'});}
  }
  window.addEventListener('message', event => {
    if (event.source !== window.parent || event.origin !== location.origin || event.data?.source !== 'zdeutsch-course') return;
    if (event.data.type === 'highlight') {
      if (!Object.hasOwn(selectors,event.data.action)) return;
      const part=event.data.part;
      if(part && !['teil-1','teil-2','teil-3','sprachbausteine-1','sprachbausteine-2'].includes(part))return;
      highlight(event.data.action,event.data.id,part);
    }
    if (event.data.type === 'hello') ready(true);
    if (event.data.type === 'clear') {activeTarget=null;activeId=null;targetElement?.classList.remove('zd-course-target');targetElement=null;clearTimeout(focusTimer);schedulePosition();}
  });
  window.addEventListener('scroll',schedulePosition,true);
  window.addEventListener('resize',schedulePosition);
  new ResizeObserver(schedulePosition).observe(document.body);
  document.addEventListener('keydown',event=>{
    if(activeTarget==='prompt' && ['Enter',' '].includes(event.key) && event.target===targetElement) {event.preventDefault();send({type:'interaction',action:'prompt'});}
  });
  document.addEventListener('click', event => {
    if (!event.isTrusted) return; // Opening a panel for a cue is not a learner's answer.
    if (activeTarget === 'prompt' && event.target.closest('.shreiben-task-columns')) send({ type: 'interaction', action: 'prompt' });
    if (activeTarget==='reading-situation' && event.target.closest('[data-reading-situation]') && !event.target.closest('select,button,a')) {
      const id=activeId, part=document.querySelector('#left-panel')?.dataset.partKey;
      setTimeout(() => send({ type: 'interaction', action: 'situation', id, part }), 80);
    }
    const button = event.target.closest('button,a');
    if (event.target.closest('a, #back-btn, #return-btn, #result-home-btn, [data-action=toggle-room-form], [data-action=start-recording], [data-action=analyze-recording]')) {
      event.preventDefault(); event.stopImmediatePropagation(); send({ type: 'navigation' }); return;
    }
    if (!button || button.disabled) return;
    const text = button.textContent.trim();
    let action = null;
    if (button.closest('#part-cards,#horen-part-list,#part-list,#part-tabs')) action = 'parts';
    else if (button.id === 'settings-btn') action = 'settings';
    else if (/^(Antworten prüfen|Prüfen|Check)$/i.test(text)) action = 'check';
    else if (button.hasAttribute('data-course-answer')) action = 'answer';
    else if (activeTarget==='reading-situation' && button.closest('[data-reading-situation]')) action = 'situation';
    else if (/^[äöüßÄÖÜẞ]$/.test(text)) action = 'characters';
    else if (button.id === 'timer-toggle') action = 'timer';
    else if (button.matches('[data-action=toggle-complete]')) action = 'complete';
    else if (button.closest('#topic-list') || button.matches('.oral-checklist-item')) action = 'topic';
    if (action) {
      const id=activeId, part=document.querySelector('#left-panel')?.dataset.partKey;
      setTimeout(() => send({ type: 'interaction', action, id, part }), 80);
    }
  }, true);
  document.addEventListener('change', event => { if (event.target.matches('input[type=radio], select[data-course-answer]')) send({ type: 'interaction', action: 'answer' }); });
  document.addEventListener('input', event => { if (event.target.matches('.shreiben-editor') && event.target.value.trim().length >= 8) send({ type: 'interaction', action: 'write' }); });
  document.addEventListener('submit', event => { event.preventDefault(); event.stopImmediatePropagation(); }, true);
  const observer = new MutationObserver(() => {ready();schedulePosition();});
  observer.observe(document.body, { subtree: true, childList: true });
  send({ type: 'booted' });
}
